import { useAppSelector } from "../state/hooks";
import { selectUser } from "../state/selectors";
import type { PermissionKey } from "../services/types/ComponentTypes";

/**
 * Returns a helper that checks whether the logged-in user has a specific
 * permission for a given screen.
 *
 * Usage:
 *   const { hasPermission } = usePermissions();
 *   hasPermission("104", "Add") // true | false
 *
 * Rules:
 *  - Returns `true` (permissive) when no screenId is given.
 *  - Returns `true` when no matching entry is found in userlevelpermissions.
 *  - Returns the stored boolean value otherwise.
 */
export function usePermissions() {
  const user = useAppSelector(selectUser);
  const permissions = user?.userlevelpermissions ?? [];
  const modulePermissions = user?.modulelevelpermissions ?? {};

  const hasPermission = (screenId: string | undefined, key: PermissionKey): boolean => {
    if (!screenId) return true;

    const entry = permissions.find((p) => String(p.ScreenId) === String(screenId));
    if (!entry) return true; // no entry → permissive default

    return Boolean(entry[key]);
  };

  const hasModulePermission = (moduleId: string | undefined): boolean => {
    if (!moduleId) return true;

    const entry = modulePermissions[moduleId];
    if (!entry) return true; // if no specific entry is found, we can default to true

    // If effective_permission is 1, hide it from UI (return false)
    return entry.effective_permission !== 1;
  };

  return { hasPermission, hasModulePermission, permissions, modulePermissions };
}
